import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import HeroSection from "./components/HeroSection";
import TrustBadges from "./components/TrustBadges";
import AboutSection from "./components/AboutSection";
import CategoryGrid from "./components/CategoryGrid";
import FeaturesSection from "./components/FeaturesSection";
import ArticlesSection from "./components/ArticlesSection";
import CTASection from "./components/CTASection";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfansspreadsheet.help";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "USFansSpreadsheet.help",
  "url": siteUrl,
  "description": "Niezależny portal informacyjny poświęcony narzędziu USFansSpreadsheet – poradniki, recenzje, porównania i informacje o bezpieczeństwie.",
  "sameAs": [
    "https://kaspreadsheet.com"
  ]
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "USFansSpreadsheet.help",
  "url": siteUrl,
  "description": "Kompletny przewodnik po USFansSpreadsheet – funkcje, bezpieczeństwo, porównania i opinie użytkowników.",
  "inLanguage": "pl-PL",
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${siteUrl}/?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Czym dokładnie jest USFansSpreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "USFansSpreadsheet to kompleksowe narzędzie do zarządzania arkuszami kalkulacyjnymi. Platforma oferuje zaawansowane funkcje analizy danych, intuicyjny interfejs oraz wsparcie dla wielu formatów plików. Jest to rozwiązanie stworzone zarówno dla początkujących użytkowników, jak i zaawansowanych analityków."
      }
    },
    {
      "@type": "Question",
      "name": "Czy USFansSpreadsheet jest bezpieczny?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tak, USFansSpreadsheet spełnia najwyższe standardy bezpieczeństwa. Platforma wykorzystuje szyfrowanie end-to-end, regularnie przechodzi audyty bezpieczeństwa i jest w pełni zgodna z regulacjami RODO."
      }
    },
    {
      "@type": "Question",
      "name": "Jak zacząć korzystać z USFansSpreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rozpoczęcie pracy z USFansSpreadsheet jest proste. Wystarczy odwiedzić oficjalną stronę, utworzyć konto i postępować zgodnie z kreatorem konfiguracji. Platforma oferuje również szereg samouczków i poradników."
      }
    },
    {
      "@type": "Question",
      "name": "Jakie są główne zalety USFansSpreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "USFansSpreadsheet wyróżnia się intuicyjnym interfejsem, zaawansowanymi funkcjami analitycznymi oraz elastycznością dostosowania do indywidualnych potrzeb. Platforma wspiera wiele formatów danych i oferuje szybkie przetwarzanie dużych zbiorów."
      }
    },
    {
      "@type": "Question",
      "name": "Czy USFansSpreadsheet ma wersję mobilną?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tak, USFansSpreadsheet oferuje w pełni funkcjonalną wersję mobilną, która pozwala na zarządzanie arkuszami z poziomu smartfona lub tabletu. Aplikacja mobilna zachowuje wszystkie kluczowe funkcje wersji desktopowej."
      }
    },
    {
      "@type": "Question",
      "name": "Gdzie mogę kupić produkty związane z USFansSpreadsheet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Wszystkie produkty i akcesoria związane z USFansSpreadsheet są dostępne w oficjalnym sklepie Kaspreadsheet. Oferujemy szeroki wybór kategorii – od odzieży po akcesoria, wszystkie w doskonałej jakości."
      }
    }
  ]
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title="USFansSpreadsheet – Kompletny przewodnik, bezpieczeństwo i porównania 2026"
        description="Poznaj USFansSpreadsheet – kompletny przewodnik po funkcjach, bezpieczeństwie i porównaniach. Sprawdź opinie użytkowników, poradniki krok po kroku i najnowsze aktualizacje 2026. Dowiedz się, jak bezpiecznie korzystać z USFansSpreadsheet."
        keywords="USFansSpreadsheet, usfans spreadsheet, arkusz kalkulacyjny, przewodnik, bezpieczeństwo, porównanie, opinie, poradnik, Polska"
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={websiteSchema} />
      <JsonLd data={faqSchema} />

      <Navbar />

      <main>
        <HeroSection />
        <TrustBadges />
        <AboutSection />
        <CategoryGrid />
        <FeaturesSection />
        <ArticlesSection />
        <CTASection />

        {/* FAQ Section */}
        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 text-center mb-12">
              Najczęściej zadawane pytania
            </h2>

            <div className="space-y-4">
              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Czym dokładnie jest USFansSpreadsheet?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  USFansSpreadsheet to kompleksowe narzędzie do zarządzania arkuszami kalkulacyjnymi. Platforma oferuje zaawansowane funkcje analizy danych, intuicyjny interfejs oraz wsparcie dla wielu formatów plików. Jest to rozwiązanie stworzone zarówno dla początkujących użytkowników, jak i zaawansowanych analityków, którzy potrzebują niezawodnego narzędzia do codziennej pracy z danymi.
                </p>
              </details>

              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Czy USFansSpreadsheet jest bezpieczny?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  Tak, USFansSpreadsheet spełnia najwyższe standardy bezpieczeństwa. Platforma wykorzystuje szyfrowanie end-to-end, regularnie przechodzi audyty bezpieczeństwa i jest w pełni zgodna z regulacjami RODO. Dane użytkowników są przechowywane na bezpiecznych serwerach z wielopoziomowym systemem ochrony przed nieautoryzowanym dostępem.
                </p>
              </details>

              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Jak zacząć korzystać z USFansSpreadsheet?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  Rozpoczęcie pracy z USFansSpreadsheet jest proste. Wystarczy odwiedzić oficjalną stronę, utworzyć konto i postępować zgodnie z kreatorem konfiguracji. Platforma oferuje również szereg samouczków i poradników, które krok po kroku przeprowadzą Cię przez wszystkie najważniejsze funkcje. Polecamy zacząć od naszego kompletnego przewodnika dostępnego na tej stronie.
                </p>
              </details>

              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Jakie są główne zalety USFansSpreadsheet?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  USFansSpreadsheet wyróżnia się intuicyjnym interfejsem, zaawansowanymi funkcjami analitycznymi oraz elastycznością dostosowania do indywidualnych potrzeb. Platforma wspiera wiele formatów danych, oferuje szybkie przetwarzanie dużych zbiorów i zapewnia dostęp z różnych urządzeń. Dodatkowo, regularne aktualizacje i wsparcie techniczne sprawiają, że jest to jedno z najbardziej niezawodnych narzędzi na rynku.
                </p>
              </details>

              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Czy USFansSpreadsheet ma wersję mobilną?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  Tak, USFansSpreadsheet oferuje w pełni funkcjonalną wersję mobilną, która pozwala na zarządzanie arkuszami z poziomu smartfona lub tabletu. Aplikacja mobilna zachowuje wszystkie kluczowe funkcje wersji desktopowej, zapewniając wygodny dostęp do danych w dowolnym miejscu i czasie.
                </p>
              </details>

              <details className="group bg-background-100 rounded-lg p-5 md:p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-heading text-lg font-semibold text-foreground-950 list-none">
                  Gdzie mogę kupić produkty związane z USFansSpreadsheet?
                  <div className="w-6 h-6 flex items-center justify-center ml-4 flex-shrink-0">
                    <i className="ri-add-line text-accent-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-500 hidden group-open:block"></i>
                  </div>
                </summary>
                <p className="mt-4 text-foreground-600 text-sm leading-relaxed">
                  Wszystkie produkty i akcesoria związane z USFansSpreadsheet są dostępne w oficjalnym sklepie Kaspreadsheet. Oferujemy szeroki wybór kategorii — od odzieży po akcesoria, wszystkie w doskonałej jakości. Skorzystaj z przycisków kategorii na tej stronie lub odwiedź bezpośrednio sklep, aby zapoznać się z pełną ofertą.
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}